import React, { FC, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronDown as ExpandMoreIcon } from "mdi-material-ui";
import { IPeriodoEtapa, ISistemaAvaliacao } from "@deltasge/marauders-map";
import { useAppSelector } from "store";
import { format, isAfter, parseISO } from "date-fns";

interface Props {
  selecionado?: IPeriodoEtapa;
  etapas?: IPeriodoEtapa[];
  onClick: (etapa?: number) => void;
  render: "accordion" | "autocomplete";
  mostrarTodos: boolean;
  sistemaAvaliacao: ISistemaAvaliacao;
  comRecuperacao?: boolean;
}

export const FilterEtapa: FC<Props> = ({
  selecionado,
  onClick,
  etapas,
  render,
  mostrarTodos,
  sistemaAvaliacao,
  comRecuperacao = false,
}) => {
  const {
    anoLetivo: { anoLetivo },
  } = useAppSelector((state) => state);

  const [panel, setPanel] = useState(false);

  const handleClick = (etapa?: number) => {
    onClick(etapa);
    if (render == "accordion") setPanel(false);
  };

  let label = `INDEPENDENTE DO ${sistemaAvaliacao.nomeEtapa} - 01/01/${anoLetivo} à 31/12/${anoLetivo}`;
  if (!mostrarTodos && !selecionado) {
    label = `SELECIONE UM ${sistemaAvaliacao.nomeEtapa?.toUpperCase()}`;
  } else if (
    selecionado &&
    selecionado.dataEtapaIni &&
    selecionado.dataEtapaFim
  ) {
    const dataIni = format(parseISO(selecionado.dataEtapaIni), "dd/MM/yy");
    let dataFim = format(parseISO(selecionado.dataEtapaFim), "dd/MM/yy");
    if (comRecuperacao && selecionado.dataRecuperacaoFim) {
      if (
        isAfter(
          parseISO(selecionado.dataRecuperacaoFim),
          parseISO(selecionado.dataEtapaFim)
        )
      ) {
        dataFim = format(parseISO(selecionado.dataRecuperacaoFim), "dd/MM/yy");
      }
    }

    label = `${selecionado.etapa} º ${sistemaAvaliacao.nomeEtapaAbreviado} - ${dataIni} à ${dataFim}`;
  }

  if (render == "accordion") {
    return (
      <Accordion
        elevation={0}
        expanded={panel}
        onChange={() => setPanel(!panel)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Etapa</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption">{label}</Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {mostrarTodos && (
              <ListItemButton onClick={() => handleClick()}>
                <ListItemIcon>
                  <Radio checked={undefined == selecionado} />
                </ListItemIcon>
                <ListItemText
                  primary="INDEPENDENTE DA ETAPA"
                  secondary={`01/01/${anoLetivo} à 31/12/${anoLetivo}`}
                />
              </ListItemButton>
            )}
            {etapas?.map((etapa) => {
              const primaryText = `${etapa.etapa}º ${sistemaAvaliacao.nomeEtapa}`;
              let labelRec: JSX.Element | undefined = undefined;
              if (
                etapa.dataRecuperacaoIni &&
                etapa.dataRecuperacaoFim &&
                comRecuperacao
              ) {
                labelRec = (
                  <>
                    <br />
                    Recup: {format(parseISO(etapa.dataRecuperacaoIni), "dd/MM")}
                    &nbsp;à&nbsp;
                    {format(parseISO(etapa.dataRecuperacaoFim), "dd/MM")}
                  </>
                );
              }
              const secondaryText = (
                <>
                  {format(parseISO(etapa.dataEtapaIni), "dd/MM/yy")}
                  &nbsp;à&nbsp;
                  {format(parseISO(etapa.dataEtapaFim), "dd/MM/yy")}
                  {labelRec}
                </>
              );

              return (
                <ListItemButton
                  key={`filter-etapa-${etapa.etapa}`}
                  onClick={() => handleClick(etapa.etapa)}
                >
                  <ListItemIcon>
                    <Radio checked={etapa.etapa == selecionado?.etapa} />
                  </ListItemIcon>
                  <ListItemText
                    primary={primaryText}
                    secondary={secondaryText}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Autocomplete
      sx={{ m: 1 }}
      disablePortal
      id="filter-etapa"
      options={etapas ?? []}
      value={selecionado}
      renderInput={(params) => <TextField {...params} label="Etapa" />}
      getOptionLabel={(option) =>
        `${option.etapa}º ${sistemaAvaliacao.nomeEtapa}`
      }
      onChange={(event, value) => handleClick(value?.etapa)}
      noOptionsText="Selecione uma série"
      clearText="Todos as turmas"
      openText="Selecione uma turma"
      renderOption={(props, option) => (
        <ListItem {...props}>
          <ListItemText
            key={option.id}
            primary={`${option.etapa}º ${sistemaAvaliacao.nomeEtapa}`}
            primaryTypographyProps={{
              noWrap: true,
            }}
          />
        </ListItem>
      )}
    />
  );
};
